'use client';

import { FunctionComponent } from 'react';
import { Box, Button } from '@mui/material';
import ProductBoxInsideCompareBar from './ProductBoxInsideCompareBar';
import { BasePartParameterValue } from '../../../schemas/BasePartParameterValue';
import { DetectedUniquePart } from '../../../schemas/DetectedUniquePart';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';
import { createPortal } from 'react-dom';

// 定義 union type
export type CompareItem = BasePartParameterValue | DetectedUniquePart;

interface CompareBarProps {
    compareItems: CompareItem[];
    handleClearAllCompareItems: () => void;
    clearCompareItemByPn: (basePn: string) => void;
    onCompareClick: () => void; // 新增這個 prop
    contentTree: ProductsContent;
}

const CompareBar: FunctionComponent<CompareBarProps> = (props) => {
    const { isMaxScreen, isScreenXxl, isScreenXl } = useResponsive();

    const dynamicButtonWidth = (() => {
        if (isMaxScreen || isScreenXxl || isScreenXl) return '250px';
        else return '200px';
    })();

    if (!props.compareItems.length) {
        return null;
    }

    return createPortal(
        <Box
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100vw',
                height: '150px',
                backgroundColor: '#FDF5F0',
                p: 1,
                zIndex: 9999,
                boxShadow: '0px -5px 4px 0px #00000040',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5, // 各區塊間的間隔
                    margin: '0 auto',
                }}
            >
                {[0, 1, 2].map((index) => {
                    const compareItem = props.compareItems[index];
                    // 依據 compareItem 的屬性決定 key 與 test id
                    const itemKey =
                        compareItem && 'basePn' in compareItem
                            ? compareItem.basePn
                            : compareItem?.uniquePn || index;
                    return (
                        <ProductBoxInsideCompareBar
                            key={`${itemKey}-product-box`}
                            compareItem={compareItem}
                            data-testid={`${itemKey}-product-box`}
                            onClearCompareItem={
                                compareItem ? props.clearCompareItemByPn : undefined
                            }
                            contentTree={props.contentTree}
                        />
                    );
                })}
                {/* 第四個區塊：垂直排列的按鈕 */}
                <Box
                    sx={{
                        flexShrink: 0,
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: 'stretch', // 讓內部元件寬度一致
                    }}
                >
                    <Button
                        disabled={props.compareItems.length < 2}
                        onClick={props.onCompareClick}
                        sx={{
                            width: dynamicButtonWidth,
                            height: '40px',
                            borderRadius: '5px',
                            backgroundColor: '#B75332', // 正常狀態背景
                            color: '#fff', // 正常狀態文字顏色
                            transition: 'none',
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            fontSize: '1rem',
                            // Hover 狀態
                            '&:hover': {
                                background: 'linear-gradient(0deg, #B75332 0%, #E58869 100%)',
                                transition: 'none',
                            },
                            // Disabled 狀態
                            '&.Mui-disabled': {
                                backgroundColor: '#CACBCD',
                                color: '#fff', // 可依需求修改 disabled 狀態文字顏色
                            },
                        }}
                        variant="contained"
                    >
                        {props.contentTree.comparison.compareButtonLabel.content}
                    </Button>
                    <Button
                        onClick={props.handleClearAllCompareItems}
                        sx={{
                            width: dynamicButtonWidth,
                            height: '40px',
                            borderRadius: '5px',
                            backgroundColor: 'transparent',
                            color: '#B75332', // 正常狀態文字顏色
                            '&:hover': {
                                color: '#E5673E',
                                transition: 'none',
                            },
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            fontSize: '1rem',
                        }}
                        variant="outlined"
                    >
                        {props.contentTree.comparison.removeAllButtonLabel.content}
                    </Button>
                </Box>
            </Box>
        </Box>,
        document.body
    );
};

export default CompareBar;
